// src/pages/CreatorHomePage.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import CreatorNav from "../components/CreatorNav";
import CreatorTopMenuBar from "../components/CreatorTopMenuBar";
import CreatorMobileBottomNav from "../components/CreatorMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import CreateAuthorTitleModal from "../components/CreateAuthorTitleModal";
import OutlineViewerModalV3 from "../components/OutlineViewerModalV3";
import UploadCompletionModal from "../components/UploadCompletionModal";
import TTSDecisionModal from "../components/TTSDecisionModal";
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import TTSStatusBanner from "../components/TTSStatusBanner";
import StreamlinedUploadFlow from "../components/StreamlinedUploadFlow";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Eye, Music } from "lucide-react";
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AuthorProfileCard from "../components/Creator/AuthorProfileCard";
import { BACKEND_URL } from '@/config/api';
import { getBookCoverUrl, hasCoverImage } from '../lib/coverUtils';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const statusColors = {
  active: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/40",
  ready: "bg-blue-500/15 text-blue-300 ring-blue-400/40",
  audio_pending: "bg-amber-500/10 text-amber-200 ring-amber-400/40",
  outline_pending: "bg-orange-500/10 text-orange-200 ring-orange-400/40",
  inactive: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
  archived: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
  draft: "bg-slate-500/15 text-slate-200 ring-slate-400/40",
};

const getStatusLabel = (status) => {
  const labels = {
    active: 'Active',
    ready: 'Ready',
    audio_pending: 'Audio Pending',
    outline_pending: 'Outline Pending',
    inactive: 'Inactive',
    archived: 'Archived',
    draft: 'Draft'
  };
  return labels[status] || 'Draft';
};

const CreatorHomePage = () => {
  const { user, logout, getHomePage } = useAuth();
  const navigate = useNavigate();
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTitleModal, setShowCreateTitleModal] = useState(false);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [selectedManuscriptId, setSelectedManuscriptId] = useState(null);
  const [pendingCompletionData, setPendingCompletionData] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [showTTSProgress, setShowTTSProgress] = useState(false);
  const [ttsProgress, setTTSProgress] = useState({ completed: 0, total: 0, isComplete: false });
  const [showStreamlinedUpload, setShowStreamlinedUpload] = useState(false);
  const [stats, setStats] = useState({
    totalTitles: 0,
    totalEngagementUnits: 0,
    totalActive: 0,
    totalReaders: 0,
    totalReaderCredits: 0,
  });

  // Fetch manuscripts data
  const fetchManuscripts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        // Let the auth system handle redirects, just stop fetching
        setLoading(false);
        return;
      }

      console.log('[CreatorHomePage] Fetching manuscripts from API...');
      const response = await axios.get(`${API}/creator/manuscripts`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _t: Date.now() },
        timeout: 10000 // 10 second timeout
      });
      console.log('[CreatorHomePage] API response received:', response.data.length, 'manuscripts');

      const data = response.data;
      console.log('Creator manuscripts fetched:', data);
      setManuscripts(data);

      // Fetch detailed stats from creator/summary endpoint
      try {
        const summaryResponse = await axios.get(`${API}/creator/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setStats({
          totalTitles: summaryResponse.data.total_books || 0,
          totalEngagementUnits: summaryResponse.data.total_engagement_units || 0,
          totalActive: summaryResponse.data.total_active_books || 0,
          totalReaders: summaryResponse.data.total_unique_readers || 0,
          totalReaderCredits: summaryResponse.data.total_reader_credits || 0,
        });
      } catch (summaryError) {
        // Fallback to calculating from manuscript data
        console.warn('Could not fetch summary, calculating from manuscripts:', summaryError);
        const totalEUs = data.reduce((sum, m) => sum + (m.total_engagement_units || 0), 0);
        const activeCount = data.filter(m => m.is_active).length;
        
        setStats({
          totalTitles: data.length,
          totalEngagementUnits: totalEUs,
          totalActive: activeCount,
          totalReaders: 0,
          totalReaderCredits: 0,
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('[CreatorHomePage] Error fetching manuscripts:', error.message);
      console.error('[CreatorHomePage] Full error:', error);
      // Show empty list rather than hanging
      setManuscripts([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchManuscripts();
      await fetchAuthorProfile();
    };
    loadInitialData();
  }, []);

  const fetchAuthorProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BACKEND_URL}/api/author/author-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists !== false) {
          setAuthorProfile(data);
        }
      }
    } catch (error) {
      console.error('Error fetching author profile:', error);
    }
  };

  // ============ TTS FUNCTIONS ============
  
  const startTTSGeneration = async (manuscriptId, devMode = false) => {
    // P0 FINAL HOLE: User-initiated TTS generation is FORBIDDEN
    // TTS is auto-triggered by the system after ingestion completes
    console.warn('[CreatorHomePage] TTS generation is backend-owned. User action not allowed.');
    toast.info('Audio generation is automatic. Please wait for processing.');
    return null;
  };
  
  // P0 FINAL HOLE: TTS polling REMOVED - Status shown via page refresh only
  // pollTTSProgress DELETED - No polling allowed
  
  // ============ END TTS FUNCTIONS ============

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    // For numbers under 1000, show 2 decimal places
    return Number(num).toFixed(2);
  };

  const formatWholeNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    // For numbers under 1000, show as whole number
    return Math.round(num).toString();
  };

  // Handle view outline for v3 manuscripts
  const handleViewOutline = (manuscriptId, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedManuscriptId(manuscriptId);
    setShowOutlineModal(true);
  };

  // Get status label for v3 manuscripts
  const getV3StatusLabel = (manuscript) => {
    if (manuscript.ingestion_version === 3) {
      // v3 manuscripts are always "processed" after upload completes
      if (manuscript.status === 'processed') {
        return 'Ready';
      }
    }
    return getStatusLabel(manuscript.status);
  };

  const handleGoToConsole = () => {
    navigate("/author-console");
  };

  const handleNewUpload = () => {
    // Open the Streamlined Upload Flow (production upload)
    setShowStreamlinedUpload(true);
  };

  const handleStreamlinedUploadComplete = () => {
    console.log('[CreatorHomePage] Upload complete callback triggered');
    // Close the upload flow
    setShowStreamlinedUpload(false);
    // Refresh manuscripts list to show the new book
    console.log('[CreatorHomePage] Calling fetchManuscripts to refresh library...');
    fetchManuscripts();
    // Show success message
    toast.success('Manuscript upload complete!');
    console.log('[CreatorHomePage] Manuscripts refresh triggered');
  };

  const handleTitleCreated = (data) => {
    console.log('[CreatorHomePage] Title created:', data);
    // Refresh manuscripts list
    fetchManuscripts();
    
    // Open outline editor for the new manuscript
    if (data.manuscriptId) {
      setPendingCompletionData({
        title: data.titleName,
        wordCount: data.wordCount,
        chapterCount: data.chapterCount
      });
      setSelectedManuscriptId(data.manuscriptId);
      setShowOutlineModal(true);
    }
  };

  const handleViewCatalog = () => {
    // Future: Could scroll to manuscript list or open filter
    // For now, just focus on the list
    document.getElementById('manuscript-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 overflow-x-hidden">
      {/* Author Navigation */}
      <CreatorNav />
      <CreatorTopMenuBar />
      
      {/* TTS Status Banner - shows when TTS jobs are active */}
      <TTSStatusBanner />
      
      {/* Main Content with mobile bottom padding */}
      <div className="pb-20 md:pb-0">

      {/* Top gradient header */}
      <header className="bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-500">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-16">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-6">
              {/* Author Headshot & Logo Stack */}
              <div className="hidden sm:flex flex-col items-center gap-3">
                {/* Headshot - doubled size (h-40 w-40 instead of h-20 w-20) */}
                {authorProfile?.headshot_image_url ? (
                  <img
                    src={`${BACKEND_URL}${authorProfile.headshot_image_url}?t=${Date.now()}`}
                    alt={authorProfile?.pen_name || user?.first_name}
                    className="h-40 w-40 rounded-full object-cover border-4 border-white/50 shadow-xl"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-40 w-40 rounded-full bg-white/20 border-4 border-white/50 flex items-center justify-center shadow-xl">
                    <span className="text-5xl font-bold text-white">
                      {(user?.first_name?.[0] || 'A').toUpperCase()}
                    </span>
                  </div>
                )}
                
                {/* Logo - below headshot, increased size by 30% */}
                {authorProfile?.logo_image_url && (
                  <img
                    src={`${BACKEND_URL}${authorProfile.logo_image_url}?t=${Date.now()}`}
                    alt={authorProfile?.publisher_name || 'Publisher Logo'}
                    className="h-[84px] max-w-[210px] object-contain bg-white/90 rounded-lg px-4 py-2 shadow-lg"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              
              <div>
                <p className="text-2xl uppercase tracking-[0.22em] text-indigo-100/90">
                  Author Dashboard
                </p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                  Welcome back{user?.first_name ? `, ${user.first_name}` : ""}.
                </h1>
                <p className="mt-3 max-w-xl text-sm sm:text-base text-indigo-100/90">
                  This is your command center for smart books. Upload, refine, and
                  track how real readers are engaging with your work in NewAudia<sup className="font-bold text-[0.6em]">TM</sup>.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center rounded-full bg-indigo-900/50 px-3 py-1 text-xs font-medium text-indigo-100 ring-1 ring-indigo-200/40">
                    Role: Author
                  </span>
                  {user?.roles?.includes("admin") && (
                    <span className="inline-flex items-center rounded-full bg-emerald-900/50 px-3 py-1 text-xs font-medium text-emerald-100 ring-1 ring-emerald-200/40">
                      Admin access enabled
                    </span>
                  )}
                  {user?.created_at && (
                    <span className="text-sm font-semibold text-indigo-100">
                      NewAudia Member Since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Author Metrics */}
            <div className="w-full lg:w-auto lg:min-w-[320px]">
              <div className="rounded-3xl bg-white/30 backdrop-blur-md p-4 ring-2 ring-white/50 shadow-xl">
                <h2 className="text-xl font-bold uppercase tracking-[0.18em] text-slate-900">
                  Author metrics
                </h2>
                <p className="mt-1 text-xs font-medium text-slate-800">
                  Real-time engagement data from your published books.
                </p>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-white/40 backdrop-blur-sm p-2 ring-1 ring-white/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                      Total titles
                    </p>
                    <p className="mt-1 text-xl font-bold text-slate-900">
                      {loading ? '...' : stats.totalTitles}
                    </p>
                    <p className="mt-0.5 text-[10px] font-medium text-slate-700">
                      Manuscripts uploaded
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/40 backdrop-blur-sm p-2 ring-1 ring-white/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                      Active books
                    </p>
                    <p className="mt-1 text-xl font-bold text-slate-900">
                      {loading ? '...' : stats.totalActive}
                    </p>
                    <p className="mt-0.5 text-[10px] font-medium text-slate-700">
                      Currently active in library
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/40 backdrop-blur-sm p-2 ring-1 ring-white/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                      Total readers
                    </p>
                    <p className="mt-1 text-xl font-bold text-blue-700">
                      {loading ? '...' : stats.totalReaders}
                    </p>
                    <p className="mt-0.5 text-[10px] font-medium text-slate-700">
                      Unique readers across all titles
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/40 backdrop-blur-sm p-2 ring-1 ring-white/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                      Reader credits
                    </p>
                    <p className="mt-1 text-xl font-bold text-purple-700">
                      {loading ? '...' : stats.totalReaderCredits.toFixed(2)}
                    </p>
                    <p className="mt-0.5 text-[10px] font-medium text-slate-700">
                      Total credits consumed
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8 sm:pt-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.1fr)]">
          {/* Left column: Titles + Activity */}
          <div className="space-y-8">
            {/* Titles overview - Book Summary Cards */}
            <section id="manuscript-list" className="rounded-3xl bg-slate-900/60 p-5 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <h2 className="text-lg font-semibold sm:text-xl">
                    Your titles at a glance
                  </h2>
                  <p className="mt-1 text-xs sm:text-sm text-slate-300">
                    A quick look at how your books are performing across the
                    NewAudia ecosystem.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleGoToConsole}
                  className="hidden rounded-full bg-purple-600 hover:bg-purple-700 px-4 py-2 text-xs font-semibold text-white transition-colors sm:inline-flex"
                >
                  Author Console
                </button>
              </div>

              {/* Book Summary Cards */}
              <div className="space-y-4">
                {loading ? (
                  <div className="px-4 py-8 text-center text-slate-400">
                    Loading your books...
                  </div>
                ) : manuscripts.length === 0 ? (
                  <div className="px-4 py-8 text-center rounded-2xl bg-slate-950/70 ring-1 ring-slate-800/80">
                    <p className="text-slate-400 mb-2">No manuscripts yet</p>
                    <button
                      onClick={handleNewUpload}
                      className="text-xs text-purple-400 hover:text-purple-300"
                    >
                      Upload your first book
                    </button>
                  </div>
                ) : (
                  manuscripts.map((book) => (
                    <div
                      key={book.id}
                      onClick={() => navigate(`/author/titles/${book.id}`)}
                      className="flex gap-4 p-4 rounded-2xl bg-slate-950/70 ring-1 ring-slate-800/80 hover:ring-purple-500/50 hover:bg-slate-900/70 cursor-pointer transition-all group"
                    >
                      {/* Book Cover - using shared cover utility */}
                      <div className="flex-shrink-0">
                        {hasCoverImage(book) ? (
                          <img
                            src={getBookCoverUrl(book, BACKEND_URL)}
                            alt={book.title}
                            className="w-20 h-28 sm:w-24 sm:h-32 object-cover rounded-lg shadow-lg ring-1 ring-slate-700/50"
                            onError={(e) => {
                              // Hide broken image and show placeholder
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <img
                            src={getBookCoverUrl(book, BACKEND_URL)}
                            alt={book.title}
                            className="w-20 h-28 sm:w-24 sm:h-32 object-cover rounded-lg shadow-lg ring-1 ring-slate-700/50"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        )}
                        <div className="w-20 h-28 sm:w-24 sm:h-32 bg-gradient-to-br from-purple-900/40 to-slate-800 rounded-lg items-center justify-center ring-1 ring-slate-700/50 hidden">
                          <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      </div>

                      {/* Book Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-sm sm:text-base truncate group-hover:text-purple-300 transition-colors">
                          {book.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                          by {book.author || 'Unknown Author'}
                        </p>
                        
                        {/* ISBN */}
                        {book.isbn && (
                          <p className="text-[11px] text-slate-500 mt-2">
                            ISBN: {book.isbn}
                          </p>
                        )}
                        
                        {/* Upload Date */}
                        <p className="text-[11px] text-slate-500 mt-1">
                          Uploaded: {book.created_at ? new Date(book.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          }) : 'N/A'}
                        </p>
                        
                        {/* Preview Available Badge */}
                        {book.hasSample && (
                          <div className="flex items-center gap-1 mt-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-teal-300 bg-teal-900/40 rounded border border-teal-700/50">
                              <Music className="w-3 h-3" />
                              Preview Available
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Arrow indicator */}
                      <div className="flex items-center flex-shrink-0">
                        <svg className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4 text-[11px] text-slate-400">
                <p>
                  Click any title to view detailed book information on the NewAudia<sup className="font-bold text-[0.6em]">TM</sup> platform.
                </p>
              </div>
            </section>
          </div>

          {/* Right column: Author Profile Sidebar */}
          <div className="space-y-6">
            {/* Add a Book CTA Card */}
            <div className="rounded-3xl bg-gradient-to-br from-purple-600/20 to-indigo-600/20 p-5 shadow-xl ring-1 ring-purple-500/30">
              <h3 className="text-lg font-semibold text-white mb-2">Ready to publish?</h3>
              <p className="text-sm text-slate-300 mb-4">
                Upload your manuscript and transform it into an intelligent audiobook.
              </p>
              <button
                onClick={handleNewUpload}
                className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 px-4 py-3 text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add a Book to Your Library
              </button>
            </div>
            
            <AuthorProfileCard />
          </div>

        </div>
      </main>
      </div>
      {/* End of main content wrapper */}

      {/* Mobile Bottom Navigation */}
      <CreatorMobileBottomNav />
      
      {/* Universal Footer */}
      <UniversalFooter />

      {/* Create Author Title Modal */}
      <CreateAuthorTitleModal
        isOpen={showCreateTitleModal}
        onClose={() => setShowCreateTitleModal(false)}
        onSuccess={handleTitleCreated}
        authorProfile={authorProfile}
      />

      {/* Outline Viewer Modal for v3 manuscripts */}
      <OutlineViewerModalV3
        isOpen={showOutlineModal}
        onClose={() => {
          console.log('[CreatorHomePage] Outline editor closed unexpectedly');
          setShowOutlineModal(false);
          setSelectedManuscriptId(null);
          fetchManuscripts();
        }}
        onSaveAndContinue={() => {
          console.log('[CreatorHomePage] Outline complete - showing TTS decision modal');
          setShowOutlineModal(false);
          setShowTTSModal(true);
        }}
        manuscriptId={selectedManuscriptId}
      />

      {/* TTS Decision Modal */}
      <TTSDecisionModal
        isOpen={showTTSModal}
        manuscriptData={pendingCompletionData}
        manuscript={manuscripts.find(m => m.id === selectedManuscriptId)}
        onYes={async (ttsMode) => {
          console.log('[CreatorHomePage] User selected: Generate TTS in', ttsMode, 'mode');
          
          if (!selectedManuscriptId) {
            console.error('[CreatorHomePage] ERROR: No manuscript ID selected!');
            toast.error('Error: No manuscript selected. Please try again.');
            setShowTTSModal(false);
            return;
          }
          
          // Store manuscript ID locally before state changes
          const manuscriptIdForTTS = selectedManuscriptId;
          
          setShowTTSModal(false);
          
          try {
            // P0 DIRECTIVE: Start TTS in background, then redirect to polling surface
            const devMode = ttsMode === 'dev';
            await startTTSGeneration(manuscriptIdForTTS, devMode);
            
            toast.success('Audio generation started! Redirecting to monitor progress...');
            
            // P0 DIRECTIVE: No progress modal - redirect to polling surface
            setTimeout(() => {
              navigate('/creator/home');
              fetchManuscripts(); // Refresh to show TTS status
            }, 1000);
          } catch (error) {
            console.error('[CreatorHomePage] TTS generation failed:', error);
            toast.error('Failed to start audio generation');
          }
        }}
        onNo={() => {
          console.log('[CreatorHomePage] User selected: Skip TTS');
          setShowTTSModal(false);
          setShowCompletionModal(true);
        }}
      />

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN - removed */}
      {/* TTS progress is shown via TTSStatusBanner on this page */}

      {/* Upload Completion Modal - shown after TTS decision */}
      {showCompletionModal && pendingCompletionData && (
        <UploadCompletionModal
          isOpen={showCompletionModal}
          manuscriptData={pendingCompletionData}
          enrichmentStatus="none"
          onClose={() => {
            console.log('[CreatorHomePage] Completion modal closed');
            setShowCompletionModal(false);
            setPendingCompletionData(null);
            setSelectedManuscriptId(null);
            fetchManuscripts();
          }}
        />
      )}

      {/* Streamlined Upload Flow - Production upload for authors */}
      <StreamlinedUploadFlow
        isOpen={showStreamlinedUpload}
        onClose={() => setShowStreamlinedUpload(false)}
        onComplete={handleStreamlinedUploadComplete}
      />
    </div>
  );
};

export default CreatorHomePage;
